import React, { useMemo, useState } from 'react';
import type { Edge, Node } from '@xyflow/react';

export type ArchitectureTone = 'frontend' | 'backend' | 'database' | 'cloud' | 'security' | 'bus' | 'external';

export interface ArchitectureNodeData extends Record<string, unknown> {
  category?: string;
  label: string;
  description?: string;
  tone?: ArchitectureTone;
}

export interface ArchitectureLane {
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
  tone: ArchitectureTone;
}

export interface ArchitectureLegendItem {
  label: string;
  tone: ArchitectureTone;
}

export interface ArchitectureGraphProps {
  nodes: Node<ArchitectureNodeData>[];
  edges: Edge[];
  lanes?: ArchitectureLane[];
  legend?: ArchitectureLegendItem[];
}

const NODE_WIDTH = 150;
const NODE_HEIGHT = 66;
const PADDING = 40;
const TOOLTIP_WIDTH = 380;
const TOOLTIP_MIN_HEIGHT = 154;
const TOOLTIP_MAX_HEIGHT = 260;

/** Theme variables per tone: plain SVG resolves `var()` for the active light or dark palette, so no hex lives here. */
export const TONE: Record<ArchitectureTone, { stroke: string; fill: string; text: string }> = {
  frontend: { stroke: 'var(--color-info)', fill: 'var(--color-info-muted)', text: 'var(--color-info-text)' },
  backend: { stroke: 'var(--color-success)', fill: 'var(--color-success-muted)', text: 'var(--color-success-text)' },
  database: { stroke: 'var(--color-purple)', fill: 'var(--color-purple-muted)', text: 'var(--color-purple-text)' },
  cloud: { stroke: 'var(--color-warning)', fill: 'var(--color-warning-muted)', text: 'var(--color-warning-text)' },
  security: { stroke: 'var(--color-danger)', fill: 'var(--color-danger-muted)', text: 'var(--color-danger-text)' },
  bus: { stroke: 'var(--color-teal)', fill: 'var(--color-teal-muted)', text: 'var(--color-teal-text)' },
  external: { stroke: 'var(--color-faint)', fill: 'var(--color-surface-hover)', text: 'var(--color-body)' },
};

function toneForNode(node: Node<ArchitectureNodeData>): ArchitectureTone {
  if (node.data.tone) return node.data.tone;
  if (node.type === 'data') return 'database';
  if (node.type === 'system') return 'frontend';
  if (node.type === 'process') return 'backend';
  const category = node.data.category?.toLowerCase() ?? '';
  if (category.includes('safety') || category.includes('hook') || category.includes('governance')) return 'security';
  if (category.includes('cache') || category.includes('bus') || category.includes('stream')) return 'bus';
  if (category.includes('adapter') || category.includes('provider')) return 'cloud';
  if (category.includes('database') || category.includes('graph') || category.includes('logging')) return 'database';
  return 'external';
}

function nodeBounds(nodes: Node<ArchitectureNodeData>[]) {
  const maxX = Math.max(...nodes.map((node) => node.position.x + NODE_WIDTH), 900);
  const maxY = Math.max(...nodes.map((node) => node.position.y + NODE_HEIGHT), 600);
  return { width: maxX + PADDING * 2, height: maxY + 140 };
}

function edgeEndpoints(source: Node<ArchitectureNodeData>, target: Node<ArchitectureNodeData>) {
  const sourceCenter = { x: source.position.x + NODE_WIDTH / 2, y: source.position.y + NODE_HEIGHT / 2 };
  const targetCenter = { x: target.position.x + NODE_WIDTH / 2, y: target.position.y + NODE_HEIGHT / 2 };
  const horizontal = Math.abs(targetCenter.x - sourceCenter.x) >= Math.abs(targetCenter.y - sourceCenter.y);

  if (horizontal) {
    return {
      x1: targetCenter.x >= sourceCenter.x ? source.position.x + NODE_WIDTH : source.position.x,
      y1: sourceCenter.y,
      x2: targetCenter.x >= sourceCenter.x ? target.position.x : target.position.x + NODE_WIDTH,
      y2: targetCenter.y,
    };
  }

  return {
    x1: sourceCenter.x,
    y1: targetCenter.y >= sourceCenter.y ? source.position.y + NODE_HEIGHT : source.position.y,
    x2: targetCenter.x,
    y2: targetCenter.y >= sourceCenter.y ? target.position.y : target.position.y + NODE_HEIGHT,
  };
}

function tooltipSize(node: Node<ArchitectureNodeData>) {
  const descriptionLength = node.data.description?.length ?? 0;
  const estimatedLines = Math.ceil(descriptionLength / 58);
  const height = Math.min(TOOLTIP_MAX_HEIGHT, Math.max(TOOLTIP_MIN_HEIGHT, 102 + estimatedLines * 22));
  return { width: TOOLTIP_WIDTH, height };
}

function tooltipPosition(
  node: Node<ArchitectureNodeData>,
  width: number,
  height: number,
  tooltipWidth: number,
  tooltipHeight: number,
) {
  const x = Math.max(16, Math.min(node.position.x - 112, width - tooltipWidth - 16));
  const above = node.position.y > tooltipHeight + 34;
  const y = above ? node.position.y - tooltipHeight - 18 : node.position.y + NODE_HEIGHT + 18;
  return { x, y: Math.max(16, Math.min(y, height - tooltipHeight - 16)) };
}

function edgeColor(edge: Edge): string {
  const stroke = edge.style?.stroke;
  return typeof stroke === 'string' ? stroke : 'var(--color-faint)';
}

function edgeDash(edge: Edge): string | undefined {
  const dash = edge.style?.strokeDasharray;
  return typeof dash === 'string' ? dash : undefined;
}

export const ArchitectureGraph: React.FC<ArchitectureGraphProps> = ({ nodes, edges, lanes = [], legend = [] }) => {
  const [activeNodeId, setActiveNodeId] = useState<string | null>(null);
  const bounds = useMemo(() => nodeBounds(nodes), [nodes]);
  const nodeById = useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes]);
  const activeNode = activeNodeId ? nodeById.get(activeNodeId) : undefined;
  const activeTone = activeNode ? TONE[toneForNode(activeNode)] : undefined;
  const activeTooltipSize = activeNode ? tooltipSize(activeNode) : undefined;
  const activeTooltip =
    activeNode && activeTooltipSize
      ? tooltipPosition(activeNode, bounds.width, bounds.height, activeTooltipSize.width, activeTooltipSize.height)
      : undefined;

  return (
    <div className="rounded-lg border border-card-border bg-surface-alt p-3 shadow-2xl">
      <svg
        viewBox={`0 0 ${bounds.width} ${bounds.height}`}
        role="img"
        aria-label="Conflui architecture graph"
        className="h-auto w-full"
      >
        <defs>
          <pattern id="architecture-grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="var(--color-border)" strokeWidth="1" opacity="0.6" />
          </pattern>
          <marker id="architecture-arrow" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto">
            <path d="M0,0 L0,6 L9,3 z" fill="var(--color-faint)" />
          </marker>
        </defs>
        <rect width={bounds.width} height={bounds.height} rx="18" fill="var(--color-surface-alt)" />
        <rect width={bounds.width} height={bounds.height} rx="18" fill="url(#architecture-grid)" />

        {lanes.map((lane) => {
          const tone = TONE[lane.tone];
          return (
            <g key={lane.label}>
              <rect
                x={lane.x}
                y={lane.y}
                width={lane.width}
                height={lane.height}
                rx="14"
                fill="none"
                stroke={tone.stroke}
                strokeDasharray="8 5"
                opacity="0.55"
              />
              <text
                x={lane.x + 18}
                y={lane.y + 28}
                fill={tone.stroke}
                fontSize="11"
                fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
              >
                {lane.label}
              </text>
            </g>
          );
        })}

        {edges.map((edge) => {
          const source = nodeById.get(edge.source);
          const target = nodeById.get(edge.target);
          if (!source || !target) return null;
          const points = edgeEndpoints(source, target);
          return (
            <line
              key={edge.id}
              {...points}
              stroke={edgeColor(edge)}
              strokeWidth={edge.animated ? 2 : 1.5}
              strokeDasharray={edgeDash(edge)}
              markerEnd="url(#architecture-arrow)"
              opacity={edge.animated ? 0.95 : 0.76}
            />
          );
        })}

        {nodes.map((node) => {
          const tone = TONE[toneForNode(node)];
          const category = node.data.category ?? node.type ?? 'node';
          return (
            <g
              key={node.id}
              tabIndex={0}
              role="img"
              aria-label={`${node.data.label}${node.data.description ? `: ${node.data.description}` : ''}`}
              className="cursor-help outline-hidden"
              onMouseEnter={() => setActiveNodeId(node.id)}
              onMouseLeave={() => setActiveNodeId((current) => (current === node.id ? null : current))}
              onFocus={() => setActiveNodeId(node.id)}
              onBlur={() => setActiveNodeId((current) => (current === node.id ? null : current))}
            >
              <rect
                x={node.position.x - 2}
                y={node.position.y - 2}
                width={NODE_WIDTH + 4}
                height={NODE_HEIGHT + 4}
                rx="7"
                fill="var(--color-surface)"
              />
              <rect
                x={node.position.x}
                y={node.position.y}
                width={NODE_WIDTH}
                height={NODE_HEIGHT}
                rx="6"
                fill={tone.fill}
                stroke={tone.stroke}
                strokeWidth={node.type === 'system' ? 2 : 1.5}
                strokeDasharray={node.type === 'data' ? '5 3' : undefined}
              />
              <text
                x={node.position.x + 12}
                y={node.position.y + 22}
                fill={tone.stroke}
                fontSize="9"
                fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
              >
                {String(category)}
              </text>
              <text
                x={node.position.x + 12}
                y={node.position.y + 42}
                fill="var(--color-heading)"
                fontSize="12"
                fontWeight="700"
                fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
              >
                {node.data.label.length > 18 ? `${node.data.label.slice(0, 17)}...` : node.data.label}
              </text>
              {node.data.description && (
                <text
                  x={node.position.x + 12}
                  y={node.position.y + 57}
                  fill="var(--color-muted)"
                  fontSize="8.5"
                  fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                >
                  {node.data.description.length > 24
                    ? `${node.data.description.slice(0, 23)}...`
                    : node.data.description}
                </text>
              )}
            </g>
          );
        })}

        {legend.length > 0 && (
          <g>
            {legend.map((item, index) => {
              const tone = TONE[item.tone];
              const x = PADDING + index * 132;
              const y = bounds.height - 34;
              return (
                <g key={item.label}>
                  <rect x={x} y={y - 10} width="12" height="12" rx="3" fill={tone.stroke} opacity="0.75" />
                  <text
                    x={x + 20}
                    y={y}
                    fill="var(--color-body)"
                    fontSize="9"
                    fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                  >
                    {item.label}
                  </text>
                </g>
              );
            })}
          </g>
        )}

        {activeNode && activeTone && activeTooltip && activeTooltipSize && (
          <foreignObject
            x={activeTooltip.x}
            y={activeTooltip.y}
            width={activeTooltipSize.width}
            height={activeTooltipSize.height}
            className="pointer-events-none"
          >
            <div
              className="h-full overflow-y-auto rounded-md border bg-surface px-4 py-3 text-left shadow-2xl"
              style={{
                borderColor: activeTone.stroke,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                overflowWrap: 'anywhere',
              }}
            >
              <div className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: activeTone.text }}>
                {activeNode.data.category ?? activeNode.type ?? 'node'}
              </div>
              <div className="mt-1 text-sm font-semibold text-heading">{activeNode.data.label}</div>
              {activeNode.data.description && (
                <div className="mt-2 text-[12px] leading-5 text-body">{activeNode.data.description}</div>
              )}
            </div>
          </foreignObject>
        )}
      </svg>
    </div>
  );
};
